import { FroelingData, FroelingSettingsSnapshot } from '../version.service';
import { LinkWindow } from '../shared/deep-link.service';
import {
  groupIcon, settingChangeParts, settingGroupLabel, settingHints, settingLabel, settingValue, settingWindowLines,
} from '../shared/froeling.utils';
import { Tr } from './board-catalog';
import { PdfBlock, PdfText } from './board-pdf';

/**
 * The Analyse board's settings widget ('froeling.settings', no chart): the Fröling's current settings by component
 * (labels, values and time windows as the Heizung section's settings view: froeling.utils), its neutral installer
 * hints and the change log filtered to the board's window. The settings are always the live snapshot (the
 * 'froelingSettings' frame) — a snapshot link carries none; the log is filtered to the link's window.
 */

/** The backend keeps the newest this many log entries (FroelingSettingsLog.MAX_ENTRIES): a full log reaches back
 *  only to its oldest entry, not to `since`. */
const LOG_MAX_ENTRIES = 500;

export interface SettingsRow { label: string; value: string; /** The controller's own label (tooltip). */ title: string; }
export interface SettingsGroupView {
  id: string;
  icon: string;
  title: string;
  /** The controller's own name where it differs ("Boiler 01" for the hot-water tank), else null. */
  sub: string | null;
  rows: SettingsRow[];
}
export interface SettingsChangeView { ts: number; time: string; component: string; setting: string; from: string; to: string; }

/**
 * What the log says about the window: 'none' nothing recorded yet; 'before' the window ends before the log starts
 * (no record at all); 'partial' the window starts before it (earlier changes unknown); 'full' the log covers it.
 */
export type SettingsLogCover = 'none' | 'before' | 'partial' | 'full';

export interface SettingsView {
  /** "26.10.26, 14:03": when the snapshot was fetched. */
  stand: string;
  /** "Kessel: Die Kessel-Solltemperatur …" */
  hints: string[];
  groups: SettingsGroupView[];
  /** The changes within the window, newest first. */
  changes: SettingsChangeView[];
  cover: SettingsLogCover;
  /** Since when the log covers changes (formatted; '' with cover 'none'). */
  since: string;
}

/** Time-window text that wraps between phases, never inside one ("11:00–13:00"): word joiners around the dashes
 *  (dropped in the PDF, whose wrap is by spaces anyway). */
const keepPhases = (s: string) => s.replace(/–/g, '\u2060–\u2060');

/** "26.10.26, 14:03" (as the Heizung section's settings view). */
function fmtTime(ts: number, locale: string): string {
  return new Date(ts).toLocaleString(locale, { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/** The widget's content for `win` from the live snapshot (and the reading, for the hints). */
export function settingsView(fs: FroelingSettingsSnapshot, live: FroelingData | null, win: LinkWindow, t: Tr, locale: string): SettingsView {
  const groups: SettingsGroupView[] = fs.components.map(g => {
    const title = settingGroupLabel(g, t);
    const rows: SettingsRow[] = g.settings.map(s => ({ label: settingLabel(g, s, t), value: settingValue(s, locale), title: s.label || s.key }));
    if (g.timeWindows?.length) {
      const label = t('FS_TIME_WINDOWS');
      rows.push({ label, value: settingWindowLines(g, t).map(keepPhases).join('\n'), title: label });
    }
    return { id: g.componentId, icon: groupIcon(g.type), title, sub: title !== g.name ? g.name : null, rows };
  });
  const hints = settingHints(fs, live).map(h => `${settingGroupLabel(h.group, t)}: ${t(h.hint.key, h.hint.params)}`);
  const all = fs.changes ?? [];
  const changes = all
    .filter(c => typeof c?.ts === 'number' && c.ts >= win.from && c.ts <= win.to)
    .sort((a, b) => b.ts - a.ts)
    .map(c => {
      const p = settingChangeParts(c, fs, t, locale);
      return { ts: c.ts, time: fmtTime(c.ts, locale), ...p, from: keepPhases(p.from), to: keepPhases(p.to) };
    });
  // where the log begins: `since`, or (a full log, the oldest entries dropped) its oldest entry
  let start = fs.since > 0 ? fs.since : 0;
  if (start && all.length >= LOG_MAX_ENTRIES) start = Math.max(start, Math.min(...all.map(c => c.ts)));
  const cover: SettingsLogCover = !start ? 'none' : start > win.to ? 'before' : start > win.from ? 'partial' : 'full';
  return { stand: fmtTime(fs.fetchedAt, locale), hints, groups, changes, cover, since: start ? fmtTime(start, locale) : '' };
}

/** The line below the change list (or instead of it): why it is empty / since when it is complete; null: none. */
export function settingsLogNote(v: SettingsView, t: Tr): string | null {
  switch (v.cover) {
    case 'none': return t('FS_BOARD_NO_LOG');
    case 'before': return t('FS_BOARD_BEFORE_LOG', { since: v.since });
    case 'partial': return t(v.changes.length ? 'FS_BOARD_PARTIAL' : 'FS_BOARD_NONE_PARTIAL', { since: v.since });
    case 'full': return v.changes.length ? null : t('FS_BOARD_NONE', { since: v.since });
  }
}

/** The widget in the PDF (in the order of the screen): the snapshot time, the hints, one table per component, the
 *  change log of the window. */
export function settingsPdf(title: string, v: SettingsView | null, readOnly: boolean, t: Tr): PdfText {
  if (!v) return { kind: 'text', title, blocks: [{ text: t('FS_BOARD_WAIT'), muted: true }] };
  const blocks: PdfBlock[] = [
    { text: t('FS_BOARD_STAND', { time: v.stand }) + (readOnly ? ' · ' + t('FS_BOARD_SNAPSHOT') : ''), muted: true },
  ];
  if (v.hints.length) {
    blocks.push({ text: t('FS_HINTS_TITLE'), bold: true, gap: 6 });
    for (const h of v.hints) blocks.push({ text: h, bullet: true });
  }
  for (const g of v.groups) {
    blocks.push({ table: {
      title: g.title + (g.sub ? ' · ' + g.sub : ''),
      head: [],
      rows: g.rows.map(r => ({ cells: [r.label, r.value] })),
      cols: [{ w: 0.58 }, { w: 0.42 }],
    } });
  }
  const note = settingsLogNote(v, t);
  blocks.push({ table: {
    title: t('FS_BOARD_CHANGES') + (v.changes.length ? ` (${v.changes.length})` : ''),
    head: v.changes.length ? [t('FS_BOARD_COL_TIME'), t('FS_BOARD_COL_COMPONENT'), t('FS_BOARD_COL_SETTING'), t('FS_BOARD_COL_CHANGE')] : [],
    rows: v.changes.map(c => ({ cells: [c.time, c.component, c.setting, `${c.from} → ${c.to}`] })),
    cols: [{ w: 0.17 }, { w: 0.2 }, { w: 0.3 }, { w: 0.33 }],
    ...(note ? { note } : {}),
  } });
  return { kind: 'text', title, blocks };
}
